import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, MotionConfig, Variants } from 'framer-motion';
import styled, { css } from 'styled-components';
import { useClickOutside } from '@mesa-labs/mesa-ui';
import { UI_MAX_WIDTH } from '../../../utils/constants';

import { copyToClipboard } from '../../../utils';
import BackButton from './BackButton';
import ProgressBar from './ProgressBar';
import { OnboardingEvents, trackEvent } from '../../../analytics';
import { useSelector } from '../../../redux/hooks';
import usePreloadPrograms from '../../../hooks/usePreloadPrograms';
import usePreloadServiceAgreement from '../../../hooks/usePreloadServiceAgreement';
import useSupportEmail from '../../../hooks/useSupportEmail';
import { ExtraDemoCodes } from '../../../redux/slices/demoMode';
import IdleTimeoutNotification, { IDLE_TIMEOUT_NOTIFICATION_HEIGHT } from '../../common/IdleTimeoutNotification';
import { useLocation, useOutlet } from 'react-router-dom';
import OnboardingHeader from '../../common/OnboardingHeader';
import OnboardingSubHeader from '../../common/OnboardingSubHeader';
import { FormContainerContainer } from '../../common/FormContainer';
import OnboardingHeaderContainer from './HeaderContainer';
import OnboardingGradient from './OnboardingGradient';
import { ExitTransitionProvider } from './exitTransition';

const MainContainer = styled.div<{ backgroundImage?: string }>`
  background-color: #FFFFFF;
  background-position: ${(props) => (props.backgroundImage ? 'bottom right' : 'right calc(100% + 42.5vh)')};
  background-repeat: no-repeat;
  background-size: cover;
  height: 100%;
  overflow-y: auto;
  width: 100%;

  @media (min-width: ${(props) => props.theme.breakpoints.Mobile}) {
    background-image: ${(props) => (props.backgroundImage ? `url(${props.backgroundImage})` : 'none')};
  }

  @media (min-width: ${(props) => props.theme.breakpoints.Tablet}) {
    flex-direction: row;
  }
`;

const HeaderContainer = styled.div`
  width: 100%;
  left: 50%;
  position: fixed;
  top: 0px;
  transform: translateX(-50%);
  z-index: 3;
`;

const Header = styled.div<{ useHeaderBorder: boolean }>`
  align-items: center;
  backdrop-filter: blur(20px);
  background: rgba(255, 255, 255, 0.8);
  border-bottom: 1px solid ${(props) => (props.useHeaderBorder && '#E1E4EA') || 'transparent'};
  box-sizing: border-box;
  display: flex;
  height: 72px;
  justify-content: center;
  width: 100%;
`;

const HeaderInner = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: space-between;
  max-width: ${UI_MAX_WIDTH}px;
  padding: 24px;
  width: 100%;

  @media (min-width: ${(props) => props.theme.breakpoints.Tablet}) {
    padding: 24px 44px;
  }
`;

const Logo = styled.img<{ height?: number, marginTop?: number }>`
  align-self: center;
  display: flex;
  height: ${(props) => props.height || 19}px;

  @media (min-width: ${(props) => props.theme.breakpoints.Tablet}) {
    display: block;
    margin-top: ${(props) => props.marginTop ?? 4}px;
  }
`;

const SupportContainer = styled.div`
  align-items: center;
  color: #525866;
  display: flex;
  font-size: 14px;
  font-weight: 400;
  gap: 16px;
  letter-spacing: -0.084px;
  line-height: 20px;
  position: relative;
`;

const SupportDropdown = styled(motion.div)`
  background: #FFF;
  border-radius: 6px;
  border: 1px solid #E1E4EA;
  box-shadow: 0px 16px 32px -12px rgba(14, 18, 27, 0.10);
  color: #0E121B;
  display: flex;
  flex-direction: column;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: -0.084px;
  line-height: 20px;
  padding: 8px;
  position: absolute;
  right: 0px;
  top: calc(100% + 8px);
  width: 232px;
`;

const SupportDropdownItem = styled.div<{ justifyContent?: string }>`
  align-items: center;
  cursor: pointer;
  display: flex;
  gap: 8px;
  justify-content: ${(props) => props.justifyContent || 'flex-start'};
  padding: 8px;
  transition: background 300ms ease-in-out;

  &:hover {
    background: #F5F7FA;
    border-radius: 4px;
  }

  &:first-of-type {
    margin-bottom: 8px;
  }

  &:last-of-type {
    margin-top: 8px;
  }
`;

const SupportDropdownLine = styled.div`
  background: #E1E4EA;
  height: 1px;
  width: 100%;  
`;

const SupportIcon = styled.img``;

const ActionButton = styled.a`
  background: #FFF;
  border-radius: 10px;
  border: 1px solid #E1E4EA;
  box-shadow: 0px 1px 2px 0px rgba(10, 13, 20, 0.03);
  color: #525866;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.084px;
  line-height: 20px;
  padding: 10px 14px;
  transition: background 300ms ease-in-out;

  &:hover {
    background: #F9FAFB;
  }
`;

const ActionButtonEmailDesktop = styled(ActionButton)`
  border-radius: 2px;
  box-shadow: none;
  color: #6D6790;
  display: none;
  font-size: 12px;
  letter-spacing: 0;
  line-height: 1.5;
  padding: 7px 15px;

  @media (min-width: ${(props) => props.theme.breakpoints.Tablet}) {
    display: block;
  }
`;

const ActionButtonEmailMobile = styled(ActionButton)`
  display: flex;  

  @media (min-width: ${(props) => props.theme.breakpoints.Tablet}) {
    display: none;
  }
`;

const LinkButton = styled.a`
  color: #0E121B;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.084px;
  text-decoration-line: underline;  

  &:hover {
    text-decoration-line: none;
  }
`;

const ContentContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  margin: 0px auto;
  max-width: ${UI_MAX_WIDTH}px;
  position: relative;
  width: 100%;
`;

const Content = styled.div<{ showIdleTimeoutNotification: boolean }>`
  --notification-offset: ${(props) => (props.showIdleTimeoutNotification ? `${IDLE_TIMEOUT_NOTIFICATION_HEIGHT}px` : '0px')};

  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
  padding: calc(72px + var(--notification-offset)) 20px 0px 20px;
  position: relative;
  width: 100%;

  @media (min-width: ${(props) => props.theme.breakpoints.Tablet}) {
    padding: calc(72px + var(--notification-offset) + 48px) 20px;
  }

  @media (min-width: ${(props) => props.theme.breakpoints.Laptop}) {
    padding: calc(72px + var(--notification-offset) + 48px) 40px 32px 40px;
  }

  @media (min-width: ${(props) => props.theme.breakpoints.Desktop}) {
    padding: calc(72px + var(--notification-offset) + 48px) 50px;
  }
`;

const DesktopBackButtonContainer = styled.div`
  display: none;
  left: 44px;
  margin: 0px;
  position: absolute;
  top: calc(72px + 20px);

  @media (min-width: ${(props) => props.theme.breakpoints.Tablet}) {
    top: calc(72px + 48px);
  }

  @media (min-width: 968px) {
    display: block;
  }
`;

const MobileBackButtonContainer = styled.div<{ $onboarding: boolean }>`
  display: flex;
  justify-content: flex-start;
  margin-top: 24px;
  margin-bottom: 24px;
  max-width: ${(props) => (props.$onboarding ? 600 : 680)}px; /* matches FormContainer's card width so the button aligns with the content below */
  width: 100%;

  @media (min-width: 968px) {
    display: none;
  }
`;

const SignInContainer = styled.div`
  align-items: center;
  color: #525866;
  display: flex;
  font-size: 14px;
  font-weight: 400;
  gap: 16px;
  justify-content: space-between;
  letter-spacing: -0.084px;
  line-height: 20px;

  & span {
    display: none;
  }

  @media (min-width: ${(props) => props.theme.breakpoints.Tablet}) {
    & span {
      display: block;
    }
  }
`;

const ChildrenContainer = styled(motion.div)<{ $onboarding: boolean }>`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;

  & > :first-child {
    padding-bottom: 60px;
  }

  ${(props) => props.$onboarding && css`
    ${OnboardingHeader} {
      color: #0E121B;
      letter-spacing: -0.24px;
      margin: 0px 0px 4px 0px;
      text-align: center;
    }

    ${OnboardingSubHeader} {
      color: #40396C;
      letter-spacing: 0;
      line-height: 20px;
      text-align: center;
    }

    ${OnboardingHeaderContainer} {
      border-bottom: none;
      margin-bottom: 32px;
      padding-bottom: 0px;
    }

    ${FormContainerContainer} {
      @media (min-width: ${props.theme.breakpoints.Mobile}) {
        max-width: 600px;
      }
    }
  `}
`;

const stepVariants: Variants = {
  enter: {
    filter: 'blur(4px)',
    opacity: 0,
  },
  center: {
    filter: 'blur(0px)',
    opacity: 1,
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
  },
  // The layout persists across steps, so AnimatePresence keeps the outgoing step mounted for the
  // length of this animation after the URL has already changed. Dropping pointer events stops a
  // click meant for the incoming step from landing on the one that is leaving.
  exit: {
    filter: 'blur(4px)',
    opacity: 0,
    pointerEvents: 'none',
    transition: { duration: 0.15, ease: [0.4, 0, 1, 1] },
  },
};

type OnboardingLayoutProps = {
  children?: ReactElement | ReactElement[];
  variant?: 'default' | 'onboarding';
  showProgressBar?: boolean;
  rightHeaderVariant?: 'sign_in' | 'contact_support' | 'start_over';
  showHeader?: boolean;
  useHeaderBorder?: boolean;
  customBackgroundImage?: string;
};

function OnboardingLayout({
  children,
  variant = 'default',
  showProgressBar = true,
  rightHeaderVariant = 'contact_support',
  showHeader = true,
  useHeaderBorder = true,
  customBackgroundImage,
}: OnboardingLayoutProps): ReactElement {
  const location = useLocation();
  const outlet = useOutlet();
  const isOnboardingVariant = variant === 'onboarding';
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const supportDropdownRef = useRef(null);
  const [supportDropdownOpen, setSupportDropdownOpen] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const exitTransition = useMemo(() => ({ isExiting, startExit: () => setIsExiting(true) }), [isExiting]);
  const [showCopyCheck, setShowCopyCheck] = useState(false);
  const supportEmail = useSupportEmail();

  usePreloadServiceAgreement();
  usePreloadPrograms();

  const demoMode = useSelector((state) => state.demoMode.isEnabled);
  const demoModeCode = useSelector((state) => state.demoMode.demoModeCode);
  const demoModeLogoSrc = useSelector((state) => state.demoMode.demoModeLogoSrc);

  const showIdleTimeoutNotification = location.search.includes('showIdleTimeoutNotification');

  // reset copy check after 2s:
  useEffect(() => {
    if (showCopyCheck) {
      const timeoutId = setTimeout(() => {
        setShowCopyCheck(false);
      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [showCopyCheck]);

  useEffect(() => {
    setSupportDropdownOpen(false);
  }, [location.pathname]);

  useClickOutside(supportDropdownRef, () => setSupportDropdownOpen(false));

  const { logoSrc, logoHeight, marginTop } = useMemo(() => {
    if (demoMode && demoModeLogoSrc) {
      if (demoModeCode === ExtraDemoCodes.DIRECT_DEBIT_CORRIGO_POA) {
        return {
          logoSrc: 'mesa-corrigo.svg',
          logoHeight: 28,
          marginTop: 0,
        };
      } else if (demoModeCode === ExtraDemoCodes.DIRECT_DEBIT_WORKDAY_VNDLY_POA) {
        return {
          logoSrc: 'mesa-workday-vndly.svg',
          logoHeight: 30,
          marginTop: 0,
        };
      } else if (demoModeCode === ExtraDemoCodes.TAPFIN_NET30) {
        return {
          logoSrc: 'mesa-tapfin.svg',
          logoHeight: 40,
          marginTop: 0,
        };
      } else if (demoModeCode === ExtraDemoCodes.RANDSTAD_NET30) {
        return {
          logoSrc: 'mesa-randstad.svg',
          logoHeight: 22,
          marginTop: 0,
        };
      } else if (demoModeCode === ExtraDemoCodes.KELLY_OCG_NET30) {
        return {
          logoSrc: 'mesa-kellyocg.svg',
          logoHeight: 30,
          marginTop: 0,
        };
      } else if (demoModeCode === ExtraDemoCodes.AGS_NET30) {
        return {
          logoSrc: 'mesa-ags.svg',
          logoHeight: 30,
          marginTop: 0,
        };
      }

      return {
        logoSrc: demoModeLogoSrc,
        logoHeight: 22,
        marginTop: 0,
      };
    }

    return {
      logoSrc: 'mesa.svg',
      logoHeight: 16.8,
      marginTop: 0,
    };
  }, [demoMode, demoModeCode, demoModeLogoSrc]);

  const rightHeaderVariantNode = useMemo(() => {
    switch (rightHeaderVariant) {
      case 'sign_in':
        return (
          <SignInContainer>
            <span>Already have an account?</span>
            <ActionButton
              href="/sign-in"
              type="button"
            >
              Sign In
            </ActionButton>
          </SignInContainer>
        );
      case 'start_over':
        return (
          <SignInContainer>
            <span>Wrong email?</span>
            <LinkButton href="/sign-up">Start over</LinkButton>
          </SignInContainer>
        );
      default:
      case 'contact_support':
        return (
          <SupportContainer ref={supportDropdownRef}>
            <ActionButtonEmailDesktop
              onClick={() => {
                trackEvent(OnboardingEvents.ContactSupportClick);
                setSupportDropdownOpen(!supportDropdownOpen);
              }}
              type="button"
            >
              Contact support
            </ActionButtonEmailDesktop>

            <AnimatePresence>
              {supportDropdownOpen && (
                <SupportDropdown
                  animate={{
                    opacity: [0, 1],
                    scale: [0.9, 1],
                  }}
                  exit={{
                    opacity: [1, 0],
                    scale: [1, 0.9],
                  }}
                >
                  <SupportDropdownItem>
                    <SupportIcon src="/assets/phone-support-icon.svg" />
                    <span>
                      312-883-0565
                    </span>
                  </SupportDropdownItem>

                  <SupportDropdownLine />

                  <SupportDropdownItem justifyContent="space-between">
                    <span>
                      {supportEmail}
                    </span>

                    {!showCopyCheck && (
                      <SupportIcon
                        onClick={() => copyToClipboard(supportEmail, () => {
                          trackEvent(OnboardingEvents.ContactSupportCopyEmailClick);
                          setShowCopyCheck(true);
                        })}
                        src="/assets/file-line-copy-icon.svg"
                      />
                    )}

                    {showCopyCheck && (
                      <SupportIcon
                        src="/assets/green-check-circle-icon.svg"
                      />
                    )}
                  </SupportDropdownItem>
                </SupportDropdown>
              )}
            </AnimatePresence>

            <ActionButtonEmailMobile
              href={`mailto:${supportEmail}`}
              type="button"
            >
              <img src="/assets/email-icon.svg" alt="Email icon to contact support" />
            </ActionButtonEmailMobile>
          </SupportContainer>
        );
    }
  }, [rightHeaderVariant, supportDropdownOpen, showCopyCheck, supportEmail]);

  return (
    <ExitTransitionProvider value={exitTransition}>
      <MotionConfig reducedMotion="user">
        <MainContainer ref={mainContainerRef} backgroundImage={customBackgroundImage}>
          {!customBackgroundImage && <OnboardingGradient isExiting={isExiting} />}

          {showHeader && (
            <HeaderContainer>
              {location.search.includes('showIdleTimeoutNotification') && (
                <IdleTimeoutNotification />
              )}

              <Header useHeaderBorder={useHeaderBorder}>
                <HeaderInner>
                  <Logo
                    key={logoSrc}
                    marginTop={marginTop}
                    height={logoHeight}
                    src={`/assets/${logoSrc}`}
                    alt="Mesa logo"
                  />

                  {showProgressBar && <ProgressBar />}

                  {rightHeaderVariantNode}
                </HeaderInner>
              </Header>
            </HeaderContainer>
          )}

          <ContentContainer>
            <Content showIdleTimeoutNotification={showIdleTimeoutNotification}>
              <DesktopBackButtonContainer>
                <BackButton />
              </DesktopBackButtonContainer>

              <MobileBackButtonContainer $onboarding={isOnboardingVariant}>
                <BackButton />
              </MobileBackButtonContainer>

              <AnimatePresence
                mode="wait"
                onExitComplete={() => mainContainerRef.current?.scrollTo({ top: 0 })}
              >
                <ChildrenContainer
                  $onboarding={isOnboardingVariant}
                  animate="center"
                  exit="exit"
                  initial="enter"
                  key={location.pathname}
                  style={{ height: '100%' }}
                  variants={stepVariants}
                >
                  {children || outlet}
                </ChildrenContainer>
              </AnimatePresence>
            </Content>
          </ContentContainer>
        </MainContainer>
      </MotionConfig>
    </ExitTransitionProvider>
  );
}

export default OnboardingLayout;
